import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

const Banner = () => {
	const navigate = useNavigate();
	const isAuthenticated = localStorage.getItem("token");
	const [isVisible, setIsVisible] = useState(false);

	useEffect(() => {
		// Trigger animations after component mounts
		setIsVisible(true);
	}, []);

	// Animated counter component
	const AnimatedStat = ({ value, label, delay }) => {
		const [count, setCount] = useState(0);
		const [showValue, setShowValue] = useState(false);
		const match = value.match(/^(\d+)(.*)/);
		const numericValue = match ? parseInt(match[1]) : 0;
		const suffix = match ? match[2] : value;
		const canAnimate = match !== null;

		useEffect(() => {
			if (!isVisible) return;

			const timer = setTimeout(() => {
				if (!canAnimate) {
					setShowValue(true);
					return;
				}

				let start = 0;
				const duration = 2000;
				const increment = numericValue / (duration / 16);

				const counter = setInterval(() => {
					start += increment;
					if (start >= numericValue) {
						setCount(numericValue);
						clearInterval(counter);
					} else {
						setCount(Math.floor(start));
					}
				}, 16);

				return () => clearInterval(counter);
			}, delay);

			return () => clearTimeout(timer);
		}, [numericValue, delay, canAnimate]);

		return (
			<div className="text-center">
				<div className="text-3xl sm:text-4xl font-bold text-white mb-1">
					{canAnimate ? (
						<>{count}{suffix}</>
					) : (
						showValue ? value : ""
					)}
				</div>
				<div className="text-sm text-gray-400">{label}</div>
			</div>
		);
	};

	return (
		<div className="relative overflow-hidden bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900 min-h-[75vh]">
			{/* Animated Background Blobs */}
			<div className="absolute inset-0 opacity-10">
				<div className="absolute top-0 -left-4 w-72 h-72 bg-blue-500 rounded-full mix-blend-multiply filter blur-xl animate-blob"></div>
				<div className="absolute top-0 -right-4 w-72 h-72 bg-cyan-500 rounded-full mix-blend-multiply filter blur-xl animate-blob animation-delay-2000"></div>
				<div className="absolute -bottom-8 left-20 w-72 h-72 bg-indigo-500 rounded-full mix-blend-multiply filter blur-xl animate-blob animation-delay-4000"></div>
			</div>

			{/* Grid Pattern Overlay */}
			<div className="absolute inset-0 bg-[linear-gradient(to_right,#ffffff08_1px,transparent_1px),linear-gradient(to_bottom,#ffffff08_1px,transparent_1px)] bg-[size:4rem_4rem]"></div>

			<div className="relative container mx-auto px-4 sm:px-6 lg:px-8 pt-24 pb-16 md:pt-32 md:pb-24">
				<div className="max-w-4xl mx-auto text-center xl:text-left xl:mx-0 xl:max-w-2xl 2xl:max-w-3xl">
					{/* Main Heading */}
					<h1
						className={`text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold text-white mb-6 leading-tight transition-all duration-700 delay-100 ${
							isVisible
								? "opacity-100 translate-y-0"
								: "opacity-0 translate-y-8"
						}`}
					>
						Vehicle Appraisals
						<span className="block mt-3 bg-gradient-to-r from-cyan-400 to-blue-500 bg-clip-text text-transparent animate-pulse-slow">
							Made Simple
						</span>
					</h1>

					{/* Subheading */}
					<p
						className={`text-lg sm:text-xl text-gray-300 mb-10 max-w-2xl mx-auto leading-relaxed transition-all duration-700 delay-200 ${
							isVisible
								? "opacity-100 translate-y-0"
								: "opacity-0 translate-y-8"
						}`}
					>
						Get accurate market valuations, compare vehicles, and generate
						professional PDF reports in minutes. The modern tool for insurance
						adjusters and appraisers.
					</p>

					{/* CTA Buttons */}
					<div
						className={`flex flex-col sm:flex-row items-center justify-center xl:justify-start gap-4 mb-16 transition-all duration-700 delay-300 ${
							isVisible
								? "opacity-100 translate-y-0"
								: "opacity-0 translate-y-8"
						}`}
					>
						<button
							onClick={() =>
								navigate(isAuthenticated ? "/form_page" : "/register")
							}
							className="group w-full sm:w-auto px-8 py-4 bg-gradient-to-r from-cyan-500 to-blue-600 text-white font-semibold rounded-xl hover:from-cyan-600 hover:to-blue-700 transition-all shadow-lg shadow-blue-500/25 hover:shadow-blue-500/40 transform hover:-translate-y-1 hover:scale-105"
						>
							<span className="flex items-center justify-center gap-2">
								{isAuthenticated ? "Go to Dashboard" : "Get Started Free"}
								<svg
									className="w-5 h-5 transition-transform group-hover:translate-x-1"
									fill="none"
									stroke="currentColor"
									viewBox="0 0 24 24"
								>
									<path
										strokeLinecap="round"
										strokeLinejoin="round"
										strokeWidth={2}
										d="M17 8l4 4m0 0l-4 4m4-4H3"
									/>
								</svg>
							</span>
						</button>
						<button
							onClick={() => navigate("/subscription")}
							className="w-full sm:w-auto px-8 py-4 bg-white/10 backdrop-blur-sm text-white font-semibold rounded-xl border border-white/20 hover:bg-white/20 transition-all hover:scale-105"
						>
							View Pricing
						</button>
					</div>

					{/* Stats with animated counters */}
					<div
						className={`grid grid-cols-2 md:grid-cols-4 gap-8 max-w-3xl mx-auto transition-all duration-700 delay-500 ${
							isVisible
								? "opacity-100 translate-y-0"
								: "opacity-0 translate-y-8"
						}`}
					>
						<AnimatedStat value="10K+" label="Vehicles Appraised" delay={600} />
						<AnimatedStat value="99%" label="Accuracy Rate" delay={700} />
						<AnimatedStat value="5 min" label="Avg. Report Time" delay={800} />
						<AnimatedStat value="24/7" label="Data Access" delay={900} />
					</div>
				</div>

				{/* Floating Cards Preview */}
				<div className="hidden xl:block absolute right-0 2xl:-right-10 top-1/2 transform -translate-y-1/2">
					<div className="relative">
						{/* Card 1 - Floats up and down */}
						<div
							className={`w-72 h-48 bg-white/10 backdrop-blur-md rounded-2xl border border-white/20 p-4 shadow-2xl animate-float transition-all duration-1000 delay-700 ${
								isVisible
									? "opacity-100 translate-x-0"
									: "opacity-0 translate-x-20"
							}`}
						>
							<div className="flex items-center gap-3 mb-3">
								<div className="w-10 h-10 bg-gradient-to-br from-cyan-400 to-blue-500 rounded-lg flex items-center justify-center animate-pulse-slow">
									<svg
										className="w-5 h-5 text-white"
										fill="none"
										stroke="currentColor"
										viewBox="0 0 24 24"
									>
										<path
											strokeLinecap="round"
											strokeLinejoin="round"
											strokeWidth={2}
											d="M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
										/>
									</svg>
								</div>
								<div>
									<div className="text-white font-semibold text-sm">
										Market Analysis
									</div>
									<div className="text-gray-400 text-xs">Real-time data</div>
								</div>
							</div>
							<div className="h-20 bg-gradient-to-r from-cyan-500/20 to-blue-500/20 rounded-lg overflow-hidden">
								{/* Animated bar chart */}
								<div className="flex items-end justify-around h-full p-2 gap-1">
									<div
										className="w-4 bg-cyan-400/60 rounded-t animate-pulse-slow"
										style={{ height: "60%" }}
									></div>
									<div
										className="w-4 bg-cyan-400/60 rounded-t animate-pulse-slow animation-delay-200"
										style={{ height: "80%" }}
									></div>
									<div
										className="w-4 bg-cyan-400/60 rounded-t animate-pulse-slow animation-delay-400"
										style={{ height: "45%" }}
									></div>
									<div
										className="w-4 bg-cyan-400/60 rounded-t animate-pulse-slow animation-delay-600"
										style={{ height: "90%" }}
									></div>
									<div
										className="w-4 bg-cyan-400/60 rounded-t animate-pulse-slow animation-delay-800"
										style={{ height: "70%" }}
									></div>
								</div>
							</div>
						</div>

						{/* Card 2 - Floats in opposite direction */}
						<div
							className={`absolute -left-8 top-20 w-64 h-44 bg-white/10 backdrop-blur-md rounded-2xl border border-white/20 p-4 shadow-2xl animate-float-reverse transition-all duration-1000 delay-900 ${
								isVisible
									? "opacity-100 translate-x-0"
									: "opacity-0 translate-x-20"
							}`}
						>
							<div className="flex items-center gap-3 mb-3">
								<div className="w-10 h-10 bg-gradient-to-br from-emerald-400 to-teal-500 rounded-lg flex items-center justify-center">
									<svg
										className="w-5 h-5 text-white"
										fill="none"
										stroke="currentColor"
										viewBox="0 0 24 24"
									>
										<path
											strokeLinecap="round"
											strokeLinejoin="round"
											strokeWidth={2}
											d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
										/>
									</svg>
								</div>
								<div>
									<div className="text-white font-semibold text-sm">
										PDF Report
									</div>
									<div className="text-gray-400 text-xs">Generated</div>
								</div>
							</div>
							<div className="space-y-2">
								<div className="h-3 bg-white/20 rounded w-full animate-shimmer"></div>
								<div className="h-3 bg-white/20 rounded w-3/4 animate-shimmer animation-delay-200"></div>
								<div className="h-3 bg-white/20 rounded w-1/2 animate-shimmer animation-delay-400"></div>
							</div>
						</div>
					</div>
				</div>
			</div>

			{/* Bottom Wave with animation */}
			<div className="absolute bottom-0 left-0 right-0">
				<svg
					className="w-full h-24 fill-white"
					viewBox="0 0 1440 74"
					preserveAspectRatio="none"
				>
					<path d="M0,32L48,37.3C96,43,192,53,288,58.7C384,64,480,64,576,58.7C672,53,768,43,864,42.7C960,43,1056,53,1152,53.3C1248,53,1344,43,1392,37.3L1440,32L1440,74L1392,74C1344,74,1248,74,1152,74C1056,74,960,74,864,74C768,74,672,74,576,74C480,74,384,74,288,74C192,74,96,74,48,74L0,74Z"></path>
				</svg>
			</div>
		</div>
	);
};

export default Banner;
